import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  McCard,
  McTabBar,
  McTab,
  McTable,
  McTag,
  McButton,
  McNotification,
  McModal,
} from '@maersk-global/mds-react-wrapper';
import { queryService } from '../../../services/queryService';
import { OverrideVersion, TerminalOverride } from '../../../types';

interface OverrideVersionsTabProps {
  queryId: string;
  terminalOverrides: TerminalOverride[];
}

// Inner component: version history for one terminal
const TerminalVersionHistory: React.FC<{ queryId: string; terminalId: string }> = ({
  queryId,
  terminalId,
}) => {
  const queryClient = useQueryClient();
  const [confirmVersion, setConfirmVersion] = useState<number | null>(null);
  const [setCurrentError, setSetCurrentError] = useState<string | null>(null);

  const {
    data: versions = [],
    isLoading,
    error,
  } = useQuery<OverrideVersion[]>({
    queryKey: ['overrideVersions', queryId, terminalId],
    queryFn: () => queryService.getOverrideVersionHistory(queryId, terminalId),
    staleTime: 1000 * 30,
  });

  const setCurrentMutation = useMutation({
    mutationFn: (versionNumber: number) =>
      queryService.setCurrentOverrideVersion(queryId, terminalId, versionNumber),
    onSuccess: () => {
      setConfirmVersion(null);
      setSetCurrentError(null);
      queryClient.invalidateQueries({ queryKey: ['overrideVersions', queryId, terminalId] });
      queryClient.invalidateQueries({ queryKey: ['terminalOverrides', queryId] });
    },
    onError: (err: any) => {
      setConfirmVersion(null);
      const msg = err?.response?.data?.message || err?.message || 'Failed to set current version';
      setSetCurrentError(msg);
    },
  });

  if (isLoading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        Loading override version history…
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '16px' }}>
        {/* @ts-ignore */}
        <McNotification appearance="error">
          Failed to load override versions for {terminalId}. Please try again.
        </McNotification>
      </div>
    );
  }

  if (versions.length === 0) {
    return (
      <div style={{ padding: '16px' }}>
        {/* @ts-ignore */}
        <McNotification appearance="info">
          No override versions found for terminal <strong>{terminalId}</strong>.
        </McNotification>
      </div>
    );
  }

  return (
    <>
      {setCurrentError && (
        <div style={{ marginBottom: '16px' }}>
          {/* @ts-ignore */}
          <McNotification appearance="error">{setCurrentError}</McNotification>
        </div>
      )}

      <McTable
        data={versions}
        columns={[
          { id: 'versionNumber', label: 'Version' },
          { id: 'sqlPreview', label: 'SQL Preview' },
          { id: 'createdBy', label: 'Created By' },
          { id: 'createdAt', label: 'Created At' },
          { id: 'isCurrent', label: 'Current' },
          { id: 'actions', label: 'Actions' },
        ]}
        datakey="versionNumber"
        style={{ maxHeight: '400px' }}
      >
        {versions.map((v: OverrideVersion) => (
          <React.Fragment key={v.versionNumber}>
            <div slot={`${v.versionNumber}_sqlPreview`}>
              <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                {v.sqlContent.length > 80
                  ? v.sqlContent.slice(0, 80) + '…'
                  : v.sqlContent}
              </span>
            </div>
            <div slot={`${v.versionNumber}_createdAt`}>
              {v.createdAt ? new Date(v.createdAt).toLocaleString() : '—'}
            </div>
            <div slot={`${v.versionNumber}_isCurrent`}>
              {v.isCurrent && (
                <McTag label="Current" appearance="success" fit="small" />
              )}
            </div>
            <div slot={`${v.versionNumber}_actions`}>
              {!v.isCurrent && (
                <McButton
                  variant="outlined"
                  appearance="secondary"
                  fit="small"
                  disabled={setCurrentMutation.isPending}
                  onClick={() => {
                    setSetCurrentError(null);
                    setConfirmVersion(v.versionNumber);
                  }}
                >
                  Set Current
                </McButton>
              )}
            </div>
          </React.Fragment>
        ))}
      </McTable>

      {/* Confirmation modal */}
      {confirmVersion !== null && (
        <McModal
          open
          heading={`Set Version ${confirmVersion} as Current?`}
          // @ts-ignore
          closed={() => setConfirmVersion(null)}
        >
          <p>
            This will make version <strong>{confirmVersion}</strong> the active override for terminal{' '}
            <strong>{terminalId}</strong>. The previous current version will be kept in history.
          </p>
          <McButton
            slot="primaryAction"
            variant="filled"
            appearance="primary"
            disabled={setCurrentMutation.isPending}
            onClick={() => setCurrentMutation.mutate(confirmVersion!)}
          >
            {setCurrentMutation.isPending ? 'Setting…' : 'Confirm'}
          </McButton>
          <McButton
            slot="secondaryAction"
            variant="outlined"
            appearance="secondary"
            onClick={() => setConfirmVersion(null)}
          >
            Cancel
          </McButton>
        </McModal>
      )}
    </>
  );
};

// Outer component: one sub-tab per terminal
export const OverrideVersionsTab: React.FC<OverrideVersionsTabProps> = ({
  queryId,
  terminalOverrides,
}) => {
  const [activeTab, setActiveTab] = useState(0);

  if (terminalOverrides.length === 0) {
    return (
      <McCard>
        <div style={{ padding: '24px' }}>
          {/* @ts-ignore */}
          <McNotification appearance="info">
            No terminal overrides are configured for this query.
          </McNotification>
        </div>
      </McCard>
    );
  }

  const activeTerminalId = terminalOverrides[activeTab]?.terminalId;

  return (
    <McCard>
      <div style={{ padding: '24px' }}>
        <McTabBar
          currentindex={activeTab}
          tabchange={(e: any) => setActiveTab(e.detail)}
        >
          {terminalOverrides.map((o) => (
            <McTab key={o.terminalId} slot="tab" label={`⚡ ${o.terminalId}`} />
          ))}
        </McTabBar>

        <div style={{ marginTop: '20px' }}>
          {activeTerminalId && (
            <TerminalVersionHistory
              key={activeTerminalId}
              queryId={queryId}
              terminalId={activeTerminalId}
            />
          )}
        </div>
      </div>
    </McCard>
  );
};
